import { useEffect, useState } from 'react';

interface TurnTimerProps {
  /** ms epoch deadline. null hides the timer. */
  deadlineAt: number | null;
  /** Whose turn it is right now — used to colour the ring. */
  isMine: boolean;
}

const TIMEOUT_MS = 30_000;

export function TurnTimer({ deadlineAt, isMine }: TurnTimerProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (deadlineAt === null) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [deadlineAt]);

  if (deadlineAt === null) return null;

  const remainingMs = Math.max(0, deadlineAt - now);
  const remainingSec = Math.ceil(remainingMs / 1000);
  const ratio = Math.max(0, Math.min(1, remainingMs / TIMEOUT_MS));

  // Compute a sweeping ring via SVG circle dasharray.
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - ratio);

  // Warn when we're getting close.
  const urgent = remainingSec <= 5;
  const stroke = urgent ? 'var(--color-coral)' : isMine ? 'var(--color-gold)' : 'var(--color-ivory)';

  return (
    <div className="relative grid h-9 w-9 place-items-center" aria-label={`Осталось ${remainingSec} секунд`}>
      <svg className="absolute inset-0" viewBox="0 0 36 36">
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="color-mix(in oklab, var(--color-ivory) 18%, transparent)"
          strokeWidth="2"
        />
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 18 18)"
          style={{ transition: 'stroke-dashoffset 0.25s linear, stroke 0.2s ease' }}
        />
      </svg>
      <span
        className="text-xs"
        style={{
          fontFamily: 'var(--font-display)',
          color: stroke,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {remainingSec}
      </span>
    </div>
  );
}
